import React from 'react';
import { Controller, Control, UseFormWatch, FieldErrors } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { RequiredIndicator } from '@/components/shared';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { maritalStatusOptions } from '@/data/commonDataOptions';

interface FiliationAndMaritalStatusFieldsetProps {
    context: CollaboratorFormContext;
    control: Control<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors?: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    layout?: 'full' | 'compact';
}

const FiliationAndMaritalStatusFieldset: React.FC<FiliationAndMaritalStatusFieldsetProps> = ({
    context,
    control,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full'
}) => {
    // Watch para dados atuais do formulário
    const formData = watch();

    // Watch específico para estado civil para lógica condicional
    const maritalStatus = watch('maritalStatus');

    // Helper para verificar se campo deve aparecer usando helper global
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper global para verificar se campos são obrigatórios
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

    // Helper para verificar se o cônjuge é necessário baseado no estado civil
    const shouldShowSpouseName = () => {
        // Só mostra se o campo spouseName está configurado para aparecer no contexto
        if (!shouldShow('spouseName')) return false;

        // E se o estado civil exige cônjuge
        return maritalStatus === 'Casado(a)' || maritalStatus === 'União Estável';
    };

    // Helper para verificar se nome do cônjuge é obrigatório
    const isSpouseNameRequired = () => {
        // Se não deve aparecer, não é obrigatório
        if (!shouldShowSpouseName()) return false;

        // Se aparece, é obrigatório (baseado na regra de negócio)
        return true;
    };

    // Helper local para obter mensagens de erro
    const getErrorMessage = (fieldName: string): string | undefined => {
        const fieldError = errors[fieldName as keyof FieldErrors<CollaboratorFormData>];
        if (fieldError && typeof fieldError === 'object' && 'message' in fieldError) {
            return fieldError.message as string;
        }
        return undefined;
    };

    return (
        <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">
                Filiação e Estado Civil
            </legend>
            <div className={`pt-2 ${layout === 'compact' ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}`}>
                {/* Nome da Mãe */}
                {shouldShow('motherName') && (
                    <div>
                        <label htmlFor="motherName" className="block text-sm font-medium text-gray-700">
                            Nome da Mãe
                            {isRequired('motherName') && <RequiredIndicator />}
                        </label>
                        <Controller
                            name="motherName"
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    value={field.value || ''}
                                    id="motherName"
                                    type="text"
                                    className="input-primary w-full mt-1"
                                    disabled={isSubmitting}
                                />
                            )}
                        />
                        {getErrorMessage('motherName') && (
                            <p className="mt-1 text-sm text-red-600">
                                {getErrorMessage('motherName')}
                            </p>
                        )}
                    </div>
                )}

                {/* Nome do Pai */}
                {shouldShow('fatherName') && (
                    <div>
                        <label htmlFor="fatherName" className="block text-sm font-medium text-gray-700">
                            Nome do Pai
                            {isRequired('fatherName') && <RequiredIndicator />}
                        </label>
                        <Controller
                            name="fatherName"
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    value={field.value || ''}
                                    id="fatherName"
                                    type="text"
                                    className="input-primary w-full mt-1"
                                    disabled={isSubmitting}
                                />
                            )}
                        />
                        {getErrorMessage('fatherName') && (
                            <p className="mt-1 text-sm text-red-600">
                                {getErrorMessage('fatherName')}
                            </p>
                        )}
                    </div>
                )}

                {/* Estado Civil */}
                {shouldShow('maritalStatus') && (
                    <div>
                        <label htmlFor="maritalStatus" className="block text-sm font-medium text-gray-700">
                            Estado Civil
                            {isRequired('maritalStatus') && <RequiredIndicator />}
                        </label>
                        <Controller
                            name="maritalStatus"
                            control={control}
                            render={({ field }) => (
                                <select
                                    {...field}
                                    value={field.value || ''}
                                    id="maritalStatus"
                                    className="input-primary w-full mt-1"
                                    disabled={isSubmitting}
                                >
                                    <option value="">Selecione...</option>
                                    {maritalStatusOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            )}
                        />
                        {getErrorMessage('maritalStatus') && (
                            <p className="mt-1 text-sm text-red-600">
                                {getErrorMessage('maritalStatus')}
                            </p>
                        )}
                    </div>
                )}

                {/* Nome do Cônjuge - Campo condicional baseado no estado civil */}
                {shouldShowSpouseName() && (
                    <div>
                        <label htmlFor="spouseName" className="block text-sm font-medium text-gray-700">
                            Nome do Cônjuge
                            {isSpouseNameRequired() && <RequiredIndicator />}
                        </label>
                        <Controller
                            name="spouseName"
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    value={field.value || ''}
                                    id="spouseName"
                                    type="text"
                                    className="input-primary w-full mt-1"
                                    disabled={isSubmitting}
                                    placeholder={maritalStatus === 'Casado(a)' ? 'Nome do(a) esposo(a)' : 'Nome do(a) companheiro(a)'}
                                />
                            )}
                        />
                        {getErrorMessage('spouseName') && (
                            <p className="mt-1 text-sm text-red-600">
                                {getErrorMessage('spouseName')}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default FiliationAndMaritalStatusFieldset; 